import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import ReactGA from 'react-ga';
import { isEqual, pick, isEmpty } from 'lodash';
import Actions, { thunks } from '@redux/quickpay';
import { thunks as appThunks } from '@redux/app';
import { push as pushAction } from 'react-router-redux';
import * as reduxUtils from '@redux-core/helpers';
import { Loader } from '@components/common';
import Final from './Final';
import PaymentInfo from './PaymentInfo';
import BulkInvoiceInfo from './BulkInvoiceInfo';
import InvoiceInfo from './InvoiceInfo';
import { Header, Paid } from './components';

class QuickPay extends Component {
	constructor(props) {
		super(props);

		const {
			match : { params : { type, hash } }, USStates, getUSStates, receiveInvoice, getInvoice, resetQuickPay,
		} = props;

		this.state = { type };

		resetQuickPay();

		if (type === '1') getInvoice(hash);
		else if (type === 'bulk') getInvoice(hash, true);
		else if (type === 'paid') receiveInvoice({}, hash);

		if (USStates.length === 0) getUSStates();
	}

	static getDerivedStateFromProps = (nextProps, lastState) => {
		if (nextProps.match.params.type !== lastState.type) return { type : nextProps.match.params.type };

		return null;
	}

	componentDidMount() {
		ReactGA.event({
			category : 'Payments',
			action   : 'Opened QuickPay page',
		});
	}

	shouldComponentUpdate({
		step, USStates, invoice, sendEmailReceipt, loadingInvoice, paymentInfoButton,
	}, { type }) {
		const lastProps = this.props;
		const lastState = this.state;

		return (
			!isEqual(step, lastProps.step)
			|| !isEqual(USStates, lastProps.USStates)
			|| !isEqual(invoice, lastProps.invoice)
			|| !isEqual(sendEmailReceipt, lastProps.sendEmailReceipt)
			|| !isEqual(loadingInvoice, lastProps.loadingInvoice)
			|| !isEqual(paymentInfoButton, lastProps.paymentInfoButton)
			|| !isEqual(type, lastState.type)
		);
	}

	componentWillUnmount() {
		const { resetQuickPay } = this.props;
		resetQuickPay();
	}

	submitPaymentInfo = ({ token }) => {
		const { makeQuickPayPayment } = this.props;
		const { type } = this.state;

		this.updateProgressButton('loading');

		makeQuickPayPayment(token, type);
	}

	updateProgressButton = (value) => {
		const { updateProgressButton } = this.props;

		updateProgressButton({
			module   : 'quickpay',
			property : 'loaders.paymentInfoButton',
			value,
		});
	}

	render() {
		const {
			USStates, TimeZoneId, invoice, hash, step, receipt, sendEmailReceipt, loadingInvoice, paymentInfoButton,
			updateData, updateStep,
		} = this.props;

		const { type } = this.state;

		// If the invoice is not loaded we return null to avoid a flashing effect while the page transitions
		if (type !== 'paid' && isEmpty(invoice)) return null;

		return (
			<div>
				<If condition={type !== 'paid'}>
					<Header step={step} />
				</If>

				<div className="position-relative">
					<Choose>
						<When condition={step === 1 && type === '1'}>
							<InvoiceInfo
								invoice={invoice}
								timeZoneId={TimeZoneId}
								hash={hash}
								sendEmailReceipt={sendEmailReceipt}
								updateData={updateData}
								onSubmit={updateStep}
							/>
						</When>
						<When condition={step === 1 && type === 'bulk'}>
							<BulkInvoiceInfo
								invoice={invoice}
								timeZoneId={TimeZoneId}
								hash={hash}
								sendEmailReceipt={sendEmailReceipt}
								updateData={updateData}
								onSubmit={updateStep}
							/>
						</When>
						<When condition={step === 2}>
							<PaymentInfo
								invoice={invoice}
								timeZoneId={TimeZoneId}
								hash={hash}
								sendEmailReceipt={sendEmailReceipt}
								type={type}
								USStates={USStates}
								buttonState={paymentInfoButton}
								onSubmit={this.submitPaymentInfo}
								onReturn={updateStep}
								updateProgressButton={this.updateProgressButton}
							/>
						</When>
						<When condition={step === 3 && !isEmpty(receipt)}>
							<Final
								invoice={invoice}
								timeZoneId={TimeZoneId}
								hash={hash}
								type={type}
								receipt={receipt}
							/>
						</When>
						<When condition={type === 'paid'}>
							<Paid hash={hash} />
						</When>
					</Choose>
					<Loader active={loadingInvoice} />
				</div>
			</div>
		);
	}
}

const mapStateToProps = ({
	app      : {
		user : { TimeZoneId },
		USStates,
	},
	quickpay : {
		invoice,
		hash,
		step,
		receipt,
		inputData : { sendEmailReceipt },
		loaders   : {
			invoice : loadingInvoice,
			paymentInfoButton,
		},
	},
}) => ({
	TimeZoneId,
	USStates,
	invoice,
	hash,
	step,
	receipt,
	sendEmailReceipt,
	loadingInvoice,
	paymentInfoButton,
});

const mapDispatchToProps = (dispatch) => ({
	getUSStates          : bindActionCreators(appThunks, dispatch).getUSStates,
	push                 : bindActionCreators(pushAction, dispatch),
	updateProgressButton : bindActionCreators(reduxUtils, dispatch).updateProgressButton,
	...pick(bindActionCreators(Actions, dispatch), ['receiveInvoice', 'updateData', 'updateStep', 'resetQuickPay']),
	...pick(bindActionCreators(thunks, dispatch), ['getInvoice', 'makeQuickPayPayment']),
});

QuickPay.propTypes = {
	TimeZoneId           : PropTypes.string,
	USStates             : PropTypes.array,
	getInvoice           : PropTypes.func.isRequired,
	getUSStates          : PropTypes.func.isRequired,
	hash                 : PropTypes.string,
	invoice              : PropTypes.object,
	loadingInvoice       : PropTypes.bool,
	makeQuickPayPayment  : PropTypes.func.isRequired,
	paymentInfoButton    : PropTypes.string,
	receipt              : PropTypes.object,
	receiveInvoice       : PropTypes.func.isRequired,
	resetQuickPay        : PropTypes.func.isRequired,
	sendEmailReceipt     : PropTypes.bool,
	step                 : PropTypes.number,
	updateData           : PropTypes.func.isRequired,
	updateProgressButton : PropTypes.func.isRequired,
	updateStep           : PropTypes.func.isRequired,
	match                : PropTypes.object.isRequired,
};

QuickPay.defaultProps = {
	TimeZoneId        : '',
	USStates          : [],
	hash              : '',
	invoice           : {},
	loadingInvoice    : false,
	paymentInfoButton : '',
	receipt           : {},
	sendEmailReceipt  : false,
	step              : 1,
};

export default connect(mapStateToProps, mapDispatchToProps)(QuickPay);
